import {Injectable} from "@angular/core";
import {TranslocoService} from "@ngneat/transloco";
import {ToastrService} from "ngx-toastr";
import {MatDialog} from "@angular/material/dialog";
import {Compbaser} from "../components/compbaser";
import {StoreSelectorsService} from "./store-selectors.service";
import {IBackground, ICRUDRecord, Id, ILayoutConstraints, IPage, ISceneBlock, IPlayerProps} from "../interfaces";
import {DialogPopComponent} from "../components/dialog-pop/dialog-pop.component";
import {actionAddSceneItem, actionCRUDRecord, actionRemoveInSceneItem, actionRemoveSceneBlocks, actionRemoveScenes, actionSceneItemPlaylist, actionSelectedSceneItemId, actionSelectedSchedule, actionUpdateSceneItem} from "../store/actions/user.actions";
import {LocalStoreService} from "./local-store.service";
import {ContainerService} from "./container.service";
import {IBlockLabelProp, MimeAnomalyType} from "../containers/editor/scene-blocks/block-constants";
import {getTotalLengthPlaylist, isDigit, isLabelItem, isNullOrUndefOrEmpty, isResourceItem, isSubScene, MAX_PLAYLIST_TIME} from "../common";
import {DialogGetAssetComponent, IDialogGetAsset} from "../components/dialog-get-asset/dialog-get-asset.component";
import {SceneMimeTypes} from "../containers/editor/scene-blocks/mime-constants";
import {take} from "rxjs/operators";
import {Lib} from "../../libs/Lib";
import {Router} from "@angular/router";
import * as _ from 'lodash';
import {Subject} from "rxjs";

@Injectable()
export class SceneService extends Compbaser {
    sceneId: Id;
    players: ISceneBlock[] = [];
    studioId: string;
    sceneLayout: ILayoutConstraints;


    constructor(
        private storeSelector: StoreSelectorsService,
        private translocoService: TranslocoService,
        private router: Router,
        private localStoreService: LocalStoreService,
        private container: ContainerService,
        private toastr: ToastrService,
        protected override dialog: MatDialog
    ) {
        super(dialog);
        this.cod(
            this.storeSelector.listenSelectedSceneId().subscribe((_sceneId: Id) => {
                this.sceneId = _sceneId;
            })
        );
        this.cod(
            this.storeSelector.listenSelectedScene().subscribe((i_scene: IPlayerProps) => {
                if (i_scene && i_scene.Player) {
                    this.sceneLayout = i_scene.Player.Data[0].Layout[0].$;
                    this.players = (i_scene.Player.Data["0"]?.Scene["0"]?.Players["0"]?.Player || []) as ISceneBlock[];
                }
            })
        );
        this.studioId = this.localStoreService.getStorageValConfig('studioId');
    }


    /**
     * remove scene items by provided ids
     */
    removeSceneItems(ids: Id[], checkPrevateChild: boolean = true) {
        if (!ids || ids.length == 0) return;
        const values = ids.map((_id) => {
            return {
                key: null,
                value: _id,
                storePath: 'Data[0].Scene[0].Players[0].Player'
            };
        });

        if (checkPrevateChild) {
            // check private child
            const privateChildIds = [];
            ids.forEach((_id) => {
                const player = this.players.find((_player) => (_player.$.id == _id) && (`${_player.$.private_child}` === 'true'));
                if (player) {
                    privateChildIds.push(player.$.src);
                }
            });

            // if deleted item is private child scene, remove it from scene list permanently
            if (privateChildIds.length > 0) {
                this.storeSelector.dispatch(actionRemoveScenes({
                    sceneIds: privateChildIds
                }));

                const saveRecord: ICRUDRecord = {
                    studioId: this.studioId,
                    command: 'delete',
                    primary: 'player_data_id',
                    table: 'player_data',
                    body: null,
                    id: privateChildIds
                };
                this.storeSelector.dispatch(actionCRUDRecord({saveRecord}));
            }
        }

        // dispatch remove action and select scene
        this.storeSelector.dispatch(actionRemoveSceneBlocks({
            sceneId: this.sceneId,
            sceneItemId: null,
            values
        }));
    }

    /**
     *
     * @param color : decimal (0 ~ 2^24)
     * @param clearImage
     */
    setSceneBackgroundColor(color: 'transparent' | number, clearImage: boolean = true) {
        if (clearImage) {
            this.clearBackgroundImage();
        }
        if (color === 'transparent') {
            this.storeSelector.dispatch(actionRemoveInSceneItem({
                sceneId: this.sceneId,
                sceneItemId: this.sceneId,
                values: [{
                    key: 'Background',
                    value: null,
                    storePath: 'Data[0]'
                }]
            }));
        } else if (color) {
            const effectBackground: IBackground = {color: `${color}`, alpha: '1', style: ''};
            this.storeSelector.dispatch(actionUpdateSceneItem({
                sceneId: this.sceneId,
                sceneItemId: this.sceneId,
                values: [{
                    key: 'Background',
                    value: [{
                        $: effectBackground
                    }],
                    storePath: 'Data[0]',
                }]
            }));
        }
    }

    setBackgroundImage(bgInfo) {
        const {resId, resType, showConfirmDialog} = bgInfo;
        const blockAdding = this.storeSelector.getBlockLabelByFileType(resType);
        blockAdding.template.Data["0"].Resource["0"].$.resource = resId;

        const bgId = this.getBackgroundImageId();
        if (bgId) {
            // if there already exists background image
            if (showConfirmDialog) {
                const msg = this.translocoService.translate('message.changeBackgroundv2', {}, this.translocoService.getActiveLang());
                const dialogRef = this.dialog.open(DialogPopComponent, {
                    height: '200px',
                    width: '400px',
                    data: {name: msg, showCancel: true}
                });
                dialogRef.afterClosed().subscribe((r) => {
                    if (r) {
                        this.removeSceneItems([bgId], false);
                        this.addItemAsBackground(blockAdding.template);
                    }
                });
            } else {
                this.removeSceneItems([bgId], false);
                this.addItemAsBackground(blockAdding.template);
            }

        } else {
            this.addItemAsBackground(blockAdding.template);
        }
    }

    addItemAsBackground(addSceneItem) {
        if (!this.sceneLayout) return;
        const newLayout = addSceneItem.Data[0].Layout[0].$;
        addSceneItem.$.id = _.uniqueId('__');

        newLayout.x = '0';
        newLayout.y = '0';
        newLayout.width = this.sceneLayout.width;
        newLayout.height = this.sceneLayout.height;
        newLayout.locked = 'true';
        newLayout.isBackground = 'true';

        this.storeSelector.dispatch(actionAddSceneItem({
            sceneId: this.sceneId,
            sceneItemId: this.sceneId,
            updatedItemIds: [addSceneItem.$.id],
            values: [{
                key: 'Player',
                cloneValue: false,
                value: addSceneItem,
                storePath: 'Data["0"].Scene["0"].Players[0]',
                addIndex: 0
            }],
        }));

        /* Alon: 'reorder' action is now happening in the Lib immediately after the actionAddSceneItem
        setTimeout(() => {
            this.storeSelector.dispatch(actionCRUDScene({
                data: {
                    command: 'reorder',
                    sceneId: this.sceneId,
                    itemIds: []
                }
            }));
            this.storeSelector.dispatch(actionCRUDScene({
                data: {
                    command: 'update',
                    sceneId: this.sceneId,
                    itemIds: [addSceneItem.$.id]
                }
            }));
        });

         */
    }


    /**
     * Check if current first bottom item is background or not
     * @returns player id
     */
    getBackgroundImageId() {
        if (this.players && this.players.length > 0) {
            const layout = this.players["0"].Data["0"].Layout["0"].$;
            if (layout && String(layout.isBackground) === 'true' && String(layout.locked) === 'true') {
                return this.players["0"].$.id;
            }
        }
        return '';
    }

    /**
     * Clear background image if exists
     */
    clearBackgroundImage() {
        const bgId = this.getBackgroundImageId();
        if (bgId) {
            this.removeSceneItems([bgId], false);
        }
    }

    /**
     * Update the layout of scene item.
     * If isBgImage is true, update the layout of background image
     */
    updateSceneItemLayout(sceneItemId: Id, newLayout: any, isBgImage: boolean = false) {
        if (!newLayout) return;
        if (isBgImage) {
            sceneItemId = this.getBackgroundImageId();
        }
        if (!sceneItemId) return;

        const values = Object.keys(newLayout).map((key) => ({
            key,
            value: newLayout[key],
            storePath: 'Data["0"].Layout["0"].$',
            cloneValue: true
        }));
        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: this.sceneId,
            sceneItemId,
            values
        }));
    }

    /**
     * Clear selection
     */
    selectRootScene() {
        this.container.activeSceneItemIds.next([this.sceneId]);
        this.storeSelector.dispatch(actionSelectedSceneItemId({id: this.sceneId}));
    }

    getBlockNameBySceneId(i_sceneId): string {
        if (!i_sceneId) return null;
        const sceneItem = this.players.find((_player) => _player.$?.id == i_sceneId);
        if (!sceneItem) return null;
        // const label = this.storeSelector.getBlockLabelById(sceneItem.$.player, false);
        const block: IBlockLabelProp = this.storeSelector.getBlockLabelById(sceneItem.$.player, false);
        return block.name;
    }

    /**
     * Check if selected scene item is nested scene or not
     * @param id
     * @returns
     */
    checkNestedSceneById(id) {
        if (!id) return '';
        const sceneItem = this.players.find((_player) => _player.$.id == id);
        if (!sceneItem) return '';

        if (isSubScene(sceneItem)) {
            return sceneItem.$.src;
        } else {
            const sceneSrc = this.getSrcFromMimeSceneItem(sceneItem);
            return !sceneSrc || sceneSrc == '-1' ? '' : sceneSrc;
        }
    }

    /**
     * Check if selected scene item is resource or not
     * @param id
     * @returns
     */
    checkResourceItemById(id) {
        if (!id) return '';

        const player = this.players.find((_player) => _player.$.id == id);
        if (isResourceItem(player)) {
            return player.Data["0"].Resource["0"]?.$.resource;
        } else {
            return '';
        }
    }

    /**
     * Check if selected scene item is label or not
     * @param id
     * @returns
     */
    checkLabelById(id) {
        if (!id) return false;
        const player = this.players.find((_player) => _player.$.id == id);
        return isLabelItem(player);
    }

    updateLastModified(sceneId, modified) {
        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: `${sceneId}`,
            sceneItemId: `${sceneId}`,
            values: [{
                key: 'lastModified',
                value: modified,
                storePath: '$'
            }],
            disableCRUD: true
        }));
    }

    private getSrcFromMimeSceneItem(sceneItem) {
        switch (this.handleMimeAnomalies(sceneItem)) {
            case "STOCKS": {
                return sceneItem.Data[0]?.Stocks?.[0]?.Player[0]?.$.src;
            }
            case "XML": {
                return sceneItem.Data[0]?.Xml?.[0]?.Player[0]?.$.src;
            }
            case "CATALOG": {
                return sceneItem.Data[0]?.Catalog?.[0]?.Player[0]?.$.src;
            }
            case "CUSTOM_RSS": {
                if (sceneItem.Data[0]?.Rss?.[0]?.CustomRss) {
                    return sceneItem.Data[0]?.Rss?.[0]?.CustomRss[0]?.Player[0]?.$.src;
                } else {
                    return sceneItem.Data[0]?.Rss?.[1]?.CustomRss[0]?.Player[0]?.$.src;
                }
            }
            default: {
                return sceneItem.Data?.[0]?.Json?.[0]?.Player?.[0]?.$?.src;
            }
        }
    }

    private handleMimeAnomalies(sceneItem): MimeAnomalyType {
        const blockLabelRecords = this.storeSelector.blockLabels;
        switch (sceneItem?.$?.player) {
            case blockLabelRecords.STOCKS.id: {
                return 'STOCKS';
            }
            case blockLabelRecords.XML.id: {
                return 'XML';
            }
            case blockLabelRecords.CATALOG.id: {
                return 'CATALOG';
            }
            case blockLabelRecords.CUSTOMRSS.id: {
                return 'CUSTOM_RSS';
            }
            default: {
                const playerId = this.storeSelector.isBlockIdJsonAliased(sceneItem);
                switch (playerId) {
                    // case blockLabelRecords.NETFEED_NEWS.id: {
                    //     return 'NETFEED_NEWS';
                    // }
                    // case blockLabelRecords.NETFEED_HOROSCOPE.id: {
                    //     return 'NETFEED_HOROSCOPE';
                    // }
                    default: {
                        return 'JSON';
                    }
                }
            }
        }
    }

    /**
     * Scheduler Block specific services
     * @param
     * @returns
     */
    jumpToSchedulers(schedule_id) {
        this.localStoreService.setStorageValConfig('scheduledEnabled', '1');
        this.storeSelector.dispatch(actionSelectedSchedule({
            scheduleSelection: {
                scheduler: {
                    scheduleId: schedule_id,
                    unique: Math.random()
                }
            }
        }));
        this.router.navigateByUrl('/scheduler/editor');

        // const selectedSceneItemId$ = this.storeSelector.listenPlayerDataOfSelectedSceneItemId();
        // const schedules$ = this.storeSelector.getSchedules();
        // combineLatest([selectedSceneItemId$, schedules$])
        //     .pipe(take(1),
        //         map(([i_scene, i_schedule]) => {
        //             const sched = i_schedule.filter((v) => compareIds(v.schedule_id, i_scene.sceneItem.Data[0].Schedule[0].$.id));
        //             return sched.length === 1 ? sched[0] : null;
        //         })
        //     ).subscribe((selectedScheduler) => {
        //         if (isNullOrUndefOrEmpty(selectedScheduler)) return;
        //         this.localStoreService.setStorageValConfig('scheduledEnabled', '1');
        //
        //     }
        // );
    }

    /**
     * Playlist Block specific services
     * @returns
     */
    public openAssetDialog(mode, multiSelect, callback = null) {
        const data: IDialogGetAsset = {mode: mode, multiSelect: multiSelect};
        const dialogRefVal = this.dialog.open(DialogGetAssetComponent, {
            width: '70%',
            panelClass: 'custom-dlg-container',
            data
        });
        this.cod(
            dialogRefVal.afterClosed()
                .pipe(take(1))
                .subscribe((dialogGetAssets: IDialogGetAsset[] | IDialogGetAsset) => {
                    if (!dialogGetAssets) return;
                    if (multiSelect && !Array.isArray(dialogGetAssets)) return;
                    if (callback) {
                        callback(dialogGetAssets);
                    }
                })
        );
    }

    public openPlaylistAssociateDialog(sceneItem: ISceneBlock, addingNew, tableName: 'pageGps' | 'Page' = null) {
        const data: IDialogGetAsset = {mode: "SCENE_RESOURCE:PLAYER", mimeType: SceneMimeTypes.Player, multiSelect: true};
        const dialogRefVal = this.dialog.open(DialogGetAssetComponent, {
            width: '70%',
            panelClass: 'custom-dlg-container',
            data
        });
        this.cod(
            dialogRefVal.afterClosed()
                .pipe(take(1))
                .subscribe((dialogGetAssets: IDialogGetAsset[]) => {
                    if (!dialogGetAssets || !Array.isArray(dialogGetAssets)) return;
                    this.addPlaylistTableItem(sceneItem, dialogGetAssets);
                })
        );
    }

    public openMusicListAssociateDialog(sceneItem: ISceneBlock, addingNew, tableName: 'pageGps' | 'Page' = null) {
        const data: IDialogGetAsset = {mode: "RESOURCE:SOUND", mimeType: SceneMimeTypes.Player, multiSelect: true};
        const dialogRefVal = this.dialog.open(DialogGetAssetComponent, {
            width: '70%',
            panelClass: 'custom-dlg-container',
            data
        });
        this.cod(
            dialogRefVal.afterClosed()
                .pipe(take(1))
                .subscribe((dialogGetAssets: IDialogGetAsset[]) => {
                    if (!dialogGetAssets || !Array.isArray(dialogGetAssets)) return;
                    this.addMusicTableItem(sceneItem, dialogGetAssets);
                })
        );
    }

    public showToastOverLength() {
        this.toastr.error(this.translocoService.translate('general.tooLong24', {}, this.translocoService.getActiveLang()));
    }

    private addPlaylistTableItem(sceneItem: ISceneBlock, dialogGetAssets: IDialogGetAsset[]) {
        let pager: IPage;
        const pages = sceneItem?.Data[0]?.Playlist[0].Page && _.isArray(sceneItem.Data[0].Playlist[0].Page) ? _.cloneDeep(sceneItem.Data[0].Playlist[0].Page) : [];
        let len = getTotalLengthPlaylist(pages);

        for (const dialogGetAsset of dialogGetAssets) {
            switch (dialogGetAsset.selectedType) {
                case "RESOURCE:SOUND": {
                    const resource_type = dialogGetAsset.sourceData.resource_type.toLowerCase();
                    const playerId = (["mp3"].indexOf(resource_type) > -1) ? "3100" : "3130";
                    // const duration = this.getDuration(dialogGetAsset.sourceData?.resource_total_time);
                    pager = {
                        $: {
                            page: `${dialogGetAsset.sourceData.resource_name}`,
                            type: "resource"
                        },
                        Player: [
                            {
                                "$": {
                                    player: playerId,
                                    id: _.uniqueId('__')
                                },
                                Data: [
                                    {
                                        Resource: [
                                            {
                                                $: {
                                                    resource: dialogGetAsset.selected
                                                }
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    };
                    if (playerId === '3100') {
                        pager.$.duration = this.getDuration(sceneItem, dialogGetAsset.sourceData?.resource_total_time);
                    }
                    break;
                }

                case "RESOURCE_SELECTED": {
                    const resource_type = dialogGetAsset.sourceData.resource_type.toLowerCase();
                    const playerId = (["mp4", "mov", "m4v", "3gp"].indexOf(resource_type) > -1) ? "3100" : "3130";
                    // const duration = this.getDuration(dialogGetAsset.sourceData?.resource_total_time);
                    pager = {
                        $: {
                            page: `${dialogGetAsset.sourceData.resource_name}`,
                            type: "resource"
                        },
                        Player: [
                            {
                                "$": {
                                    player: playerId,
                                    id: _.uniqueId('__')
                                },
                                Data: [
                                    {
                                        Resource: [
                                            {
                                                $: {
                                                    resource: dialogGetAsset.selected
                                                }
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    };
                    if (playerId === '3100') {
                        pager.$.duration = this.getDuration(sceneItem, dialogGetAsset.sourceData?.resource_total_time);
                    }
                    break;
                }
                case "SCENE_SELECTED": {
                    pager = {
                        $: {
                            page: dialogGetAsset.sourceData.Player.$.label,
                            type: "scene",
                            // duration: "5"
                        },
                        Player: [
                            {
                                $: {
                                    src: dialogGetAsset.selected
                                }
                            }
                        ]
                    };
                    break;
                }
            }

            if (pager.$.duration) {
                len += parseFloat(pager.$.duration);
            }
            if (len > MAX_PLAYLIST_TIME) {
                this.showToastOverLength();
                return;
            }
            pages.push(pager);
        }
        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: this.sceneId,
            sceneItemId: sceneItem.$.id,
            values: [{
                key: 'Page',
                value: pages,
                storePath: 'Data[0].Playlist[0]'
            }]
        }));
        // super.initApplyStoreToDynamic(true);
    }

    private addMusicTableItem(sceneItem: ISceneBlock, dialogGetAssets: IDialogGetAsset[]) {
        let pager: IPage;
        const pages = sceneItem?.Data[0]?.Music[0].Page && _.isArray(sceneItem.Data[0].Music[0].Page) ? _.cloneDeep(sceneItem.Data[0].Music[0].Page) : [];
        let len = getTotalLengthPlaylist(pages);

        for (const dialogGetAsset of dialogGetAssets) {
            switch (dialogGetAsset.selectedType) {
                case "RESOURCE_SELECTED": {
                    // const resource_type = dialogGetAsset.sourceData.resource_type.toLowerCase();
                    // const playerId = (["mp3"].indexOf(resource_type) > -1) ? "4110" : "4110";
                    // const duration = this.getDuration(dialogGetAsset.sourceData?.resource_total_time);
                    const playerId = "3145";
                    pager = {
                        $: {
                            page: `${dialogGetAsset.sourceData.resource_name}`,
                            type: "resource"
                        },
                        Player: [
                            {
                                "$": {
                                    player: playerId,
                                    id: _.uniqueId('__')
                                },
                                Data: [
                                    {
                                        Resource: [
                                            {
                                                $: {
                                                    resource: dialogGetAsset.selected
                                                }
                                            }
                                        ]
                                    }
                                ]
                            }
                        ]
                    };
                    pager.$.duration = this.getDurationMusic(sceneItem, dialogGetAsset.sourceData?.resource_total_time);
                    break;
                }
                case "SCENE_SELECTED": {
                    pager = {
                        $: {
                            page: dialogGetAsset.sourceData.Player.$.label,
                            type: "scene",
                            // duration: "5"
                        },
                        Player: [
                            {
                                $: {
                                    src: dialogGetAsset.selected
                                }
                            }
                        ]
                    };
                    break;
                }
            }

            if (pager.$.duration) {
                len += parseFloat(pager.$.duration);
            }
            if (len > MAX_PLAYLIST_TIME) {
                this.showToastOverLength();
                return;
            }
            pages.push(pager);
        }
        this.storeSelector.dispatch(actionUpdateSceneItem({
            sceneId: this.sceneId,
            sceneItemId: sceneItem.$.id,
            values: [{
                key: 'Page',
                value: pages,
                storePath: 'Data[0].Music[0]'
            }]
        }));
        // super.initApplyStoreToDynamic(true);
    }

    private getDuration(sceneItem, currentDuration) {
        const defaultDuration = sceneItem.Data[0].Playlist[0].$.duration;
        if (currentDuration === '0' || isNullOrUndefOrEmpty(currentDuration)) {
            currentDuration = defaultDuration;
        }
        return String(Lib.ParseToFloatDouble(currentDuration));
    }

    private getDurationMusic(sceneItem, currentDuration) {
        const defaultDuration = sceneItem.Data[0].Music[0].$.duration;
        if (currentDuration === '0' || isNullOrUndefOrEmpty(currentDuration)) {
            currentDuration = defaultDuration;
        }
        return String(Lib.ParseToFloatDouble(currentDuration));
    }
}
